import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '@/stores';
import {
  LayoutDashboard,
  Users,
  Star,
  Package,
  Wallet,
  CheckSquare,
  MessageSquare,
  Ticket,
  Clock,
  MapPin,
  Settings,
  ChevronDown,
  Radio,
  LogOut,
  Coins,
  Bell,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { useLogout } from '@/hooks/useAuth';
import { usePermission, CanonicalPermission } from '@/hooks/usePermission';

interface SubMenuItem {
  path: string;
  labelKey: string;
  icon: any;
  permission?: CanonicalPermission | CanonicalPermission[];
}

interface MenuItem {
  path: string;
  labelKey: string;
  icon: any;
  permission?: CanonicalPermission | CanonicalPermission[];
  subItems?: SubMenuItem[];
}

export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen, setSidebarOpen, language, theme } = useThemeStore();
  const { logout } = useLogout();
  const { hasPermission, hasAnyPermission } = usePermission();

  const isRTL = language === 'ar';
  const isLight = theme === 'light';

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems: MenuItem[] = [
    { path: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
    { path: '/subscriptions', labelKey: 'nav.subscriptions', icon: Star, permission: 'subscriptions.read' },
    {
      path: '/customers',
      labelKey: 'nav.customers',
      icon: Users,
      permission: 'customers.read',
      subItems: [
        { path: '/debts', labelKey: 'nav.debts', icon: Coins, permission: 'debts.manage' }
      ]
    },
    { path: '/services-plans', labelKey: 'nav.servicesPlans', icon: Package, permission: 'subscriptions.read' },
    { path: '/wallet', labelKey: 'nav.wallet', icon: Wallet, permission: 'wallet.read' },
    { path: '/tasks', labelKey: 'nav.tasks', icon: CheckSquare, permission: 'tasks.read' },
    { path: '/requests', labelKey: 'nav.requests', icon: MessageSquare, permission: 'requests.read' },
    { path: '/tickets', labelKey: 'nav.tickets', icon: Ticket, permission: 'tickets.read' },
    { path: '/subscriber-sessions', labelKey: 'nav.subscriberSessions', icon: Radio, permission: 'customers.read' },
    { path: '/power-monitor', labelKey: 'nav.powerMonitor', icon: Zap, permission: 'tasks.read' },
    { path: '/sessions', labelKey: 'nav.sessions', icon: Clock, permission: 'customers.read' },
    { path: '/locations', labelKey: 'nav.locations', icon: MapPin, permission: 'customers.read' },
    { path: '/notifications', labelKey: 'nav.notificationCenter', icon: Bell },
    {
      path: '/settings',
      labelKey: 'nav.settings',
      icon: Settings,
      permission: 'settings.read',
      subItems: [
        { path: '/settings/users-permissions', labelKey: 'nav.usersPermissions', icon: ShieldCheck, permission: 'users.manage' },
        { path: '/alerts', labelKey: 'nav.alerts', icon: Bell, permission: 'settings.read' }
      ]
    },
  ];

  const visibleMenuItems = React.useMemo(() => {
    return menuItems
      .filter((item) => {
        if (!item.permission) return true;
        if (Array.isArray(item.permission)) {
          return hasAnyPermission(item.permission);
        }
        return hasPermission(item.permission);
      })
      .map((item) => {
        if (!item.subItems || item.subItems.length === 0) return item;
        const visibleSubItems = item.subItems.filter((sub) => {
          if (!sub.permission) return true;
          if (Array.isArray(sub.permission)) {
            return hasAnyPermission(sub.permission);
          }
          return hasPermission(sub.permission);
        });
        return { ...item, subItems: visibleSubItems };
      });
  }, [hasPermission, hasAnyPermission]);

  // Accordion state for expandable sections: Default initial state = collapsed for all sections
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    if (location.pathname === '/debts') {
      initial['/customers'] = true;
    }
    if (
      location.pathname === '/alerts' ||
      location.pathname === '/users-permissions' ||
      (location.pathname.startsWith('/settings/') && location.pathname !== '/settings')
    ) {
      initial['/settings'] = true;
    }
    return initial;
  });

  // Track previous pathname so manual collapses by user aren't overridden on every render
  const prevPathRef = React.useRef(location.pathname);

  // Auto-expand section ONLY when route transitions to one of its children
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (location.pathname === '/debts') {
        setOpenSections(prev => ({ ...prev, '/customers': true }));
      } else if (
        location.pathname === '/alerts' ||
        location.pathname === '/users-permissions' ||
        (location.pathname.startsWith('/settings/') && location.pathname !== '/settings')
      ) {
        setOpenSections(prev => ({ ...prev, '/settings': true }));
      }
    }
  }, [location.pathname]);

  const toggleSection = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenSections(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  return (
    <aside
      className="relative flex flex-col h-full"
      style={{
        width: isMobile ? '240px' : (sidebarOpen ? '240px' : '0px'),
        minWidth: isMobile ? 'auto' : (sidebarOpen ? '240px' : '0px'),
        position: isMobile ? 'fixed' : 'relative',
        top: isMobile ? 0 : 'auto',
        bottom: isMobile ? 0 : 'auto',
        right: isMobile ? (isRTL ? 0 : 'auto') : 'auto',
        left: isMobile ? (isRTL ? 'auto' : 0) : 'auto',
        height: '100%',
        zIndex: isMobile ? 1001 : 40,
        transform: isMobile ? (sidebarOpen ? 'translateX(0)' : (isRTL ? 'translateX(240px)' : 'translateX(-240px)')) : 'none',
        transition: isMobile ? 'transform 0.3s ease-in-out' : 'width 0.3s ease, min-width 0.3s ease',
        overflow: 'hidden',
        background: isLight
          ? '#ffffff'
          : 'linear-gradient(180deg, #0a1628 0%, #0d1f3c 100%)',
        borderLeft: isLight
          ? '1px solid #e2e8f0'
          : '1px solid rgba(255,255,255,0.08)',
        boxShadow: isMobile
          ? (sidebarOpen ? (isLight ? '0 0 20px rgba(0,0,0,0.15)' : '0 0 30px rgba(0,0,0,0.5)') : 'none')
          : (isLight ? '4px 0 12px rgba(0,0,0,0.06)' : 'none'),
      }}
    >
      {/* Logo */}
      <div
        style={{
          padding: '20px 16px',
          borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '10px',
          direction: 'rtl',
        }}
      >
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: isLight ? '#0f172a' : '#fff', fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>
            FTTH Nexus
          </div>
          <div style={{ color: isLight ? '#64748b' : '#64748b', fontSize: '11px' }}>Contractor Portal</div>
        </div>
        <div
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '8px',
            background: '#ffffff',
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <img
            src="/logo.png"
            alt="FTTH Nexus Logo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>

      {/* Navigation List */}
      <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto', direction: 'rtl' }}>
        {visibleMenuItems.map((item) => {
          const Icon = item.icon;
          const hasSub = item.subItems && item.subItems.length > 0;
          const isSubActive = hasSub && item.subItems?.some(s => location.pathname === s.path);
          const isParentActive = location.pathname === item.path;
          const isOpen = !!openSections[item.path];

          return (
            <div key={item.path} style={{ marginBottom: '4px' }}>
              {/* Main Parent Item */}
              <div
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setSidebarOpen(false);
                  }
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: isParentActive
                    ? isLight
                      ? 'linear-gradient(90deg, rgba(59,130,246,0.12), rgba(99,102,241,0.08))'
                      : 'linear-gradient(90deg, rgba(59,130,246,0.2), rgba(99,102,241,0.15))'
                    : isSubActive
                      ? isLight
                        ? 'rgba(59,130,246,0.05)'
                        : 'rgba(59,130,246,0.08)'
                      : 'transparent',
                  border: isParentActive
                    ? isLight
                      ? '1px solid rgba(59,130,246,0.4)'
                      : '1px solid rgba(59,130,246,0.3)'
                    : '1px solid transparent',
                  color: isParentActive || isSubActive
                    ? '#3b82f6'
                    : isLight ? '#475569' : '#94a3b8',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  direction: 'rtl',
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isParentActive) {
                    e.currentTarget.style.background = isLight
                      ? 'rgba(59,130,246,0.06)'
                      : 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.color = isLight ? '#0f172a' : '#e2e8f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isParentActive) {
                    e.currentTarget.style.background = isSubActive
                      ? (isLight ? 'rgba(59,130,246,0.05)' : 'rgba(59,130,246,0.08)')
                      : 'transparent';
                    e.currentTarget.style.color = isSubActive
                      ? '#3b82f6'
                      : (isLight ? '#475569' : '#94a3b8');
                  }
                }}
              >
                {/* Right side in RTL: Title + Icon */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Icon size={18} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '14px', fontWeight: isParentActive ? 600 : 400, whiteSpace: 'nowrap' }}>
                    {t(item.labelKey)}
                  </span>
                </div>

                {/* Left side in RTL: Accordion Chevron Toggle (if has sub-items) */}
                {hasSub && (
                  <div
                    onClick={(e) => toggleSection(item.path, e)}
                    title={isOpen ? 'طي القائمة' : 'توسيع القائمة'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      color: isLight ? '#64748b' : '#94a3b8',
                      transition: 'transform 0.25s ease, background 0.2s ease',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <ChevronDown size={15} />
                  </div>
                )}
              </div>

              {/* Sub-Items List */}
              {hasSub && isOpen && (
                <div
                  style={{
                    marginRight: isRTL ? '12px' : '0px',
                    marginLeft: isRTL ? '0px' : '12px',
                    paddingRight: isRTL ? '10px' : '0px',
                    paddingLeft: isRTL ? '0px' : '10px',
                    borderRight: isRTL
                      ? isLight ? '2px solid rgba(59,130,246,0.25)' : '2px solid rgba(59,130,246,0.3)'
                      : 'none',
                    borderLeft: !isRTL
                      ? isLight ? '2px solid rgba(59,130,246,0.25)' : '2px solid rgba(59,130,246,0.3)'
                      : 'none',
                    marginTop: '3px',
                    marginBottom: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                  }}
                >
                  {item.subItems?.map((sub) => {
                    const SubIcon = sub.icon;
                    const isSubItemActive = location.pathname === sub.path;

                    return (
                      <div
                        key={sub.path}
                        onClick={() => {
                          navigate(sub.path);
                          if (isMobile) {
                            setSidebarOpen(false);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '9px',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          background: isSubItemActive
                            ? isLight
                              ? 'linear-gradient(90deg, rgba(59,130,246,0.14), rgba(99,102,241,0.1))'
                              : 'linear-gradient(90deg, rgba(59,130,246,0.25), rgba(99,102,241,0.18))'
                            : 'transparent',
                          border: isSubItemActive
                            ? isLight
                              ? '1px solid rgba(59,130,246,0.45)'
                              : '1px solid rgba(59,130,246,0.35)'
                            : '1px solid transparent',
                          color: isSubItemActive
                            ? '#3b82f6'
                            : isLight ? '#64748b' : '#94a3b8',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          direction: 'rtl',
                          userSelect: 'none',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubItemActive) {
                            e.currentTarget.style.background = isLight
                              ? 'rgba(59,130,246,0.06)'
                              : 'rgba(255,255,255,0.06)';
                            e.currentTarget.style.color = isLight ? '#0f172a' : '#e2e8f0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSubItemActive) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = isLight ? '#64748b' : '#94a3b8';
                          }
                        }}
                      >
                        <SubIcon size={15} style={{ flexShrink: 0, opacity: isSubItemActive ? 1 : 0.85 }} />
                        <span style={{ fontSize: '13px', fontWeight: isSubItemActive ? 600 : 400, whiteSpace: 'nowrap' }}>
                          {t(sub.labelKey)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div
        style={{
          padding: '12px 8px',
          borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <button
          onClick={logout}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'rgba(239,68,68,0.12)',
            border: '1px solid rgba(239,68,68,0.3)',
            color: '#f87171',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'right',
            justifyContent: 'flex-end',
            direction: 'rtl',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239,68,68,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(239,68,68,0.12)';
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
            تسجيل الخروج
          </span>
          <LogOut size={18} style={{ flexShrink: 0 }} />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
